// src/components/GridView.js - Rebuilt with pure Tailwind CSS
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { BsUpcScan } from "react-icons/bs";
import { FiPlus, FiMinus, FiAlertTriangle, FiWifiOff, FiTrash2 } from "react-icons/fi";
import { getImageUrl } from "../utils/getImageUrl";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../hooks/useCart";
import { useProductOrderStatsContext } from "../context/ProductOrderStatsContext";
import { useProductDiscountContext } from "../context/ProductDiscountContext";
import { useCustomerContext } from "../context/CustomerContext";
import discountService from "../services/discountService";
import apiClient from "../services/api";
import useAccess from "../hooks/useAccess";
import { mostPermissive } from "../permissions/access";

// Fallback image URL
const FALLBACK_IMAGE = "/static/images/no-image.png";

const GridView = ({ products = [], isMobile = false, leftCollapsed = true, rightVisible = false, lastProductRef, onCartOperationStart, onCartOperationEnd, showStock = false }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const { selectedCustomer } = useCustomerContext();
  const { can: canAccess, stateOf: accessStateOf } = useAccess();
  const can = (key) => !isAuthenticated || canAccess(key);
  const stateOf = (key) => (isAuthenticated ? accessStateOf(key) : mostPermissive(key));

  // Use new local-first cart system
  const {
    items: cartItems,
    addItem: handleAddToCart,
    updateItem: handleUpdateItem,
    removeItem: handleRemoveFromCart,
    getCartItem,
    getQuantityInCart
  } = useCart();

  const { getOrderStatsForProduct, ensureProductsFetched } = useProductOrderStatsContext();
  const { fetchDiscountsForProducts, getDiscountBadge, getDiscountBadgeInfo, getDiscountForProduct } = useProductDiscountContext();

  const productIds = useMemo(() => products.map(p => p.id).filter(id => id != null), [products]);
  const productIdsString = useMemo(() => productIds.join(','), [productIds]);

  // Cache for customer-specific prices
  const [customerPrices, setCustomerPrices] = useState({});

  // Fetch customer-specific prices when customer or products change
  useEffect(() => {
    const fetchCustomerPrices = async () => {
      if (!selectedCustomer?.id || productIds.length === 0) {
        setCustomerPrices({});
        return;
      }

      try {
        const response = await apiClient.post('/orders/get_customer_prices/', {
          product_ids: productIds,
          customer_id: selectedCustomer.id
        });
        setCustomerPrices(response.data || {});
      } catch (error) {
        console.error('[GridView] Error fetching customer prices:', error);
        setCustomerPrices({});
      }
    };

    fetchCustomerPrices();
  }, [selectedCustomer?.id, productIdsString]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to get the effective price for a product (customer-specific or default)
  const getEffectivePrice = useCallback((product) => {
    if (selectedCustomer?.id && customerPrices[product.id?.toString()]) {
      return parseFloat(customerPrices[product.id.toString()].price);
    }
    return parseFloat(product.price || 0);
  }, [selectedCustomer?.id, customerPrices]);

  useEffect(() => {
    if (productIds.length > 0) {
      ensureProductsFetched(productIds);
      if (isAuthenticated) {
        fetchDiscountsForProducts(productIds, selectedCustomer?.id || null);
      }
    }
  }, [productIds, productIdsString, selectedCustomer?.id, ensureProductsFetched, fetchDiscountsForProducts, isAuthenticated]);

  // Local UI states
  const [expandedTitles, setExpandedTitles] = useState({});
  const [selectedUnits, setSelectedUnits] = useState({});
  const [actionStates, setActionStates] = useState({});
  const [showDiscountTooltip, setShowDiscountTooltip] = useState(null);
  
  // Calculate dynamic grid columns based on sidebar states
  const getGridColumns = useMemo(() => {
    // Mobile columns remain unchanged
    const mobileColumns = "grid-cols-2 sm:grid-cols-2";

    // When not authenticated, use full width (no sidebars)
    if (!isAuthenticated) {
      return `${mobileColumns} grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2`;
    }

    // Desktop columns based on sidebar states
    let desktopColumns;
    const bothSidebarsOpen = !leftCollapsed && rightVisible;
    const oneSidebarOpen = (!leftCollapsed && !rightVisible) || (leftCollapsed && rightVisible);
    const bothSidebarsCollapsed = leftCollapsed && !rightVisible;

    if (bothSidebarsCollapsed) {
      // 7 columns when both sidebars collapsed
      desktopColumns = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2";
    } else if (oneSidebarOpen) {
      // 6 columns when one sidebar open
      desktopColumns = "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2";
    } else if (bothSidebarsOpen) {
      // 5 columns when both sidebars open
      desktopColumns = "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2";
    } else {
      // Fallback to current default
      desktopColumns = "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2";
    }

    return `${mobileColumns} ${desktopColumns}`;
  }, [leftCollapsed, rightVisible, isAuthenticated]);
  
  // Handle unit selection
  const handleUnitSelect = useCallback(async (productId, unitSymbol, product, unitObj) => {
    try {
      onCartOperationStart?.();
      
      // Get all cart items for this product
      const productCartItems = cartItems.filter(item => item.product?.id === productId);
      
      let itemWasConverted = false;
      
      // If there are existing cart items with different units, convert them
      if (productCartItems.length > 0) {
        const existingItem = productCartItems[0]; // Take the first (should only be one)
        
        if (existingItem.unit?.symbol !== unitSymbol) {
          // Keep the same quantity number, just change the unit
          const sameQuantity = parseFloat(existingItem.quantity || 0);
          
          // Remove old item and add new one with same quantity
          console.log(`[GridView] Removing item: ${existingItem.id} (${existingItem.quantity} ${existingItem.unit?.symbol})`);
          await handleRemoveFromCart(existingItem.id);
          
          console.log(`[GridView] Adding new item: ${sameQuantity} ${unitSymbol}`);
          const unitPrice = getEffectivePrice(product) * parseFloat(unitObj.conversion_factor || 1);
          await handleAddToCart(product, unitObj, sameQuantity, unitPrice);
          
          console.log(`[GridView] Changed unit from ${existingItem.quantity} ${existingItem.unit?.symbol} to ${sameQuantity} ${unitSymbol}`);
          itemWasConverted = true;
        }
      }
      
      // Update selected unit
      setSelectedUnits(prev => ({
        ...prev,
        [productId]: unitSymbol
      }));
      
      onCartOperationEnd?.();
      
      // Return whether an item was converted (added during conversion)
      return itemWasConverted;
    } catch (error) {
      console.error('Error switching unit:', error);
      onCartOperationEnd?.();
      return false;
    }
  }, [cartItems, handleRemoveFromCart, handleAddToCart, onCartOperationStart, onCartOperationEnd, getEffectivePrice]);

  // Handle quantity changes
  const handleQuantityChange = useCallback(async (cartItem, quantityChange, productId) => {
    try {
      onCartOperationStart?.();
      setActionStates(prev => ({ ...prev, [productId]: { loading: true } }));
      
      const newQuantity = parseFloat(cartItem.quantity || 0) + quantityChange;
      
      if (newQuantity <= 0) {
        await handleRemoveFromCart(cartItem.id);
      } else {
        await handleUpdateItem(cartItem.id, newQuantity);
      }
      
      setActionStates(prev => ({ ...prev, [productId]: { success: true } }));
      onCartOperationEnd?.();
      
      // Clear success state after short delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [productId]: {} }));
      }, 300);
    } catch (error) {
      console.error('Error changing quantity:', error);
      setActionStates(prev => ({ 
        ...prev, 
        [productId]: { error: true, errorMessage: error.message } 
      }));
      
      onCartOperationEnd?.();
      
      // Clear error state after delay
      setTimeout(() => {
        setActionStates(prev => ({ ...prev, [productId]: {} }));
      }, 2000);
    }
  }, [handleUpdateItem, handleRemoveFromCart, onCartOperationStart, onCartOperationEnd]);
  
  // Check online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Close tooltip when clicking outside
  useEffect(() => {
    if (showDiscountTooltip === null) return;

    const handleClickOutside = () => {
      setShowDiscountTooltip(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDiscountTooltip]);
  
  // Initialize selectedUnits based on products and cart
  const saveUnitSelections = useCallback((selections) => {
    try {
      localStorage.setItem('unit_selections', JSON.stringify(selections));
    } catch (error) {
      console.error('Error saving unit selections to localStorage:', error);
    }
  }, []);
  
  useEffect(() => {
    const initialUnits = {};
    
    // Try to load previous selections from localStorage
    let savedSelections = {};
    try {
      const saved = localStorage.getItem('unit_selections');
      if (saved) {
        savedSelections = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading saved unit selections:', error);
    }
    
    if (Array.isArray(products)) {
      products.forEach((product) => {
        const productId = product.id;
        
        // Priority 1: Check cart items first
        const productCartItems = cartItems.filter(
          item => item.product?.id === productId
        );
        
        if (productCartItems.length > 0) {
          // Use the unit from cart with highest quantity
          const sortedItems = [...productCartItems].sort(
            (a, b) => parseFloat(b.quantity || 0) - parseFloat(a.quantity || 0)
          );
          initialUnits[productId] = sortedItems[0].unit?.symbol || '';
        } 
        // Priority 2: Check localStorage for saved preference
        else if (savedSelections[productId]) {
          initialUnits[productId] = savedSelections[productId];
        }
        // Priority 3: Default logic
        else {
          if (product.hide_base_unit && product.extra_units && product.extra_units.length > 0) {
            // Extra units have nested unit object: { id, unit: { id, name, symbol }, conversion_factor }
            initialUnits[productId] = product.extra_units[0].unit?.symbol || product.extra_units[0].symbol;
          } else {
            initialUnits[productId] = product.unit?.symbol || '';
          }
        }
      });
      
      setSelectedUnits(initialUnits);
    }
  }, [products, cartItems]);
  
  // Save unit selections when changed
  useEffect(() => {
    if (Object.keys(selectedUnits).length > 0) {
      saveUnitSelections(selectedUnits);
    }
  }, [selectedUnits, saveUnitSelections]);
  
  // Toggle product title expansion
  const toggleProductTitle = useCallback((productId) => {
    setExpandedTitles((prev) => ({ ...prev, [productId]: !prev[productId] }));
  }, []);
  
  // Handle image error
  const handleImageError = useCallback((e) => {
    e.target.onerror = null;
    e.target.src = getImageUrl(FALLBACK_IMAGE);
    e.target.classList.add("image-fallback");
  }, []);

  // Render
  if (!Array.isArray(products) || products.length === 0) {
    return <div className="text-center p-4">No products to display.</div>;
  }
  
  const showImage = can('home.col.image', 'Product image');
  const showCategory = can('home.col.category', 'Category');
  const showProductBadges = can('home.col.product_badges', 'Product badges');
  const showOrderHistory = can('home.section.order_history', 'Order history (usual / last order)');
  const showName = can('home.col.name', 'Product name');
  const showBarcode = can('home.col.barcode', 'Barcode');
  const showSku = can('home.col.sku', 'SKU');
  const showStockCol = can('home.col.stock', 'Stock');
  const showPrice = can('home.col.price', 'Price');
  const showDiscount = can('home.badge.discount', 'Discount badge');
  const showLicenceNotice = can('home.badge.licence_required', 'Premises licence notice');
  const showCartQuantity = can('home.badge.cart_quantity', 'Quantity in cart');
  const unitState = stateOf('home.field.unit', 'Unit selector');
  const quantityState = stateOf('home.field.quantity', 'Quantity');
  const addState = stateOf('home.btn.add_to_cart', 'Add to cart');
  const removeState = stateOf('home.btn.remove_from_cart', 'Remove from cart');
  
  return (
    <div className={`grid ${getGridColumns} gap-2`}>
      {products.map((product, index) => {
        // Skip invalid products
        if (!product || !product.id || !product.unit) {
          return null;
        }
        
        const productId = product.id;
        const isTitleExpanded = expandedTitles[productId] || false;
        const selectedUnitSymbol = selectedUnits[productId] || product.unit.symbol;
        const actionState = actionStates[productId] || { loading: false, success: false, error: false };
        
        // Prepare unit data - normalize extra_units which have nested structure: { id, unit: {...}, conversion_factor }
        const normalizedExtraUnits = (product.extra_units || []).map(eu => ({
          id: eu.unit?.id || eu.id,
          name: eu.unit?.name || eu.name,
          symbol: eu.unit?.symbol || eu.symbol,
          conversion_factor: eu.conversion_factor
        }));
        const availableUnits = (product.hide_base_unit
          ? normalizedExtraUnits
          : [{ ...product.unit, conversion_factor: 1 }, ...normalizedExtraUnits]
        ).filter(u => u && u.symbol);
        
        const selectedUnitObj = availableUnits.find(
          (unit) => unit.symbol === selectedUnitSymbol
        ) || availableUnits[0];
        
        // Get cart data - find any cart item for this product (should only be one unit now)
        const productCartItems = cartItems.filter(item => item.product?.id === productId);
        const selectedCartItem = productCartItems.length > 0 ? productCartItems[0] : null;
        const selectedQuantity = selectedCartItem ? parseFloat(selectedCartItem.quantity || 0) : 0;

        // Get discount info for this product
        const discountBadge = getDiscountBadge(productId);
        const discountBadgeInfo = getDiscountBadgeInfo(productId);
        const hasAppliedDiscount = selectedCartItem?.discount_percent > 0 && selectedCartItem?.discounted_price;

        const discountBadgeText = discountBadgeInfo?.badgeConfig?.customText
          || (discountBadgeInfo?.type === 'fixed_price' ? null : discountBadge);
        const discountBadgeButton = showDiscount && discountBadgeInfo && discountBadgeText && !selectedCartItem ? (
          <span className="relative inline-block">
            <button
              type="button"
              className="ml-2 text-xs px-2 py-1 rounded font-bold whitespace-nowrap cursor-pointer"
              style={{
                backgroundColor: discountBadgeInfo.badgeConfig?.color || '#DC2626',
                color: discountBadgeInfo.badgeConfig?.textColor || '#FFFFFF',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowDiscountTooltip(showDiscountTooltip === productId ? null : productId);
              }}
            >
              {discountBadgeText}
            </button>
            {showDiscountTooltip === productId && (() => {
              const discount = getDiscountForProduct(productId);
              const customDescription = discountBadgeInfo?.badgeConfig?.customDescription;
              const explanation = customDescription || (discount ? discountService.formatDiscountConditionExplanation(discount) : null);
              if (!explanation) return null;
              return (
                <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
                    <div className="font-medium mb-1">{discount?.name}</div>
                    <div>{explanation}</div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                  </div>
                </div>
              );
            })()}
          </span>
        ) : null;

        // Calculate BASE UNIT prices for display (per kg, not per tray)
        // The user wants to see "£5 → £3 (-40%) /kg" not the tray prices
        const conversionFactor = parseFloat(selectedCartItem?.unit?.conversion_factor || 1);
        const baseOriginalPrice = conversionFactor > 0
          ? parseFloat(selectedCartItem?.price_at_add || 0) / conversionFactor
          : getEffectivePrice(product);
        const baseDiscountedPrice = conversionFactor > 0 && selectedCartItem?.discounted_price
          ? parseFloat(selectedCartItem.discounted_price) / conversionFactor
          : baseOriginalPrice;

        const appliedDiscount = hasAppliedDiscount ? {
          original_price: baseOriginalPrice,  // Base unit price (e.g., per kg)
          final_price: baseDiscountedPrice,    // Discounted base unit price (e.g., per kg)
          discount_percent: parseFloat(selectedCartItem.discount_percent || 0)
        } : null;

        // Handle image
        const primaryImage =
          product.images?.find((image) => image.is_primary)?.image ||
          product.images?.[0]?.image ||
          FALLBACK_IMAGE;
        
        return (
          <div
            key={productId}
            className="mb-2"
            ref={index === products.length - 1 ? lastProductRef : null}
          >
            <div className={`bg-white shadow-sm rounded-md h-full ${actionState.loading ? 'opacity-75' : ''} transition-none`}>
              {/* Image Container with Fixed Height and Category Overlay */}
              <div className="relative h-[250px]">
                {/* Category Badge Overlay */}
                {showCategory && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="bg-gray-700/90 backdrop-blur-sm text-white text-xs px-2 py-1 rounded shadow-sm" title={product.category?.name}>
                    {product.category?.name || "Uncategorized"}
                  </span>
                </div>
                )}
                
                {/* Status Indicators & Badges - Top Right */}
                <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                  {/* Status Indicators */}
                  <div className="flex gap-1">
                    {actionState.error && (
                      <span className="bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full" title={actionState.errorMessage}>
                        <FiAlertTriangle className="w-3 h-3" />
                      </span>
                    )}
                    {!isOnline && (
                      <span className="bg-yellow-500 text-gray-900 text-xs px-1.5 py-0.5 rounded-full">
                        <FiWifiOff className="w-3 h-3" />
                      </span>
                    )}
                  </div>

                  {/* Product Badges */}
                  {showProductBadges && product.badges && product.badges.length > 0 && (
                    <div className="flex flex-col items-end gap-0.5">
                      {product.badges.filter(b => b.is_active).slice(0, 3).map((badge) => (
                        <span
                          key={badge.id}
                          className="text-[9px] px-1.5 py-0.5 rounded shadow-sm font-medium whitespace-nowrap"
                          style={{ backgroundColor: badge.color, color: badge.text_color }}
                          title={badge.name}
                        >
                          {badge.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                {showImage && (
                <img
                  src={getImageUrl(primaryImage)}
                  className="h-full w-full object-contain p-[15px] transition-none"
                  alt={product.name}
                  onError={handleImageError}
                />
                )}

                {/* Order Statistics Overlay - Bottom of Image */}
                {showOrderHistory && (() => {
                  const orderStats = getOrderStatsForProduct(productId);
                  const hasOrderHistory = orderStats && orderStats.total_orders > 0;

                  if (!hasOrderHistory) return null;

                  return (
                    <div className="absolute bottom-2 left-0 right-0 z-10">
                      <div className="flex text-white text-[0.65rem]">
                        <div className="flex-1 bg-blue-600/10 backdrop-blur-sm p-1 pb-0 text-blue-900">
                          <div className="font-semibold mb-0.5">USUAL</div>
                          <div className="text-sm font-bold">
                            {orderStats.usual_quantity || 0}{orderStats.usual_unit || ''}
                            {orderStats.usual_frequency && (
                              <> Every {orderStats.usual_frequency}d</>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 bg-green-600/10 backdrop-blur-sm p-1 pb-0 text-right text-green-900">
                          <div className="font-semibold mb-0.5">LAST ORDER</div>
                          <div className="text-sm font-bold">
                            {orderStats.last_order_date && (
                              <>{new Date(orderStats.last_order_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} </>
                            )}
                            {orderStats.last_quantity || 0}{orderStats.last_unit || ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Card Body */}
              <div className="relative flex flex-col px-2 pt-0 pb-2">
                {/* Product Title */}
                {showName && (
                <h6
                  className={`mb-1 ${isTitleExpanded ? 'expanded' : 'truncate'} min-h-[2.5em] cursor-pointer text-sm font-medium`}
                  title={product.name}
                  onClick={() => toggleProductTitle(productId)}
                >
                  {product.name || "Unnamed Product"}
                </h6>
                )}
                
                {/* Product Info */}
                {(showBarcode || showSku) && (
                <div className="flex justify-between items-center mb-1">
                  {showBarcode && (
                  <small className="text-gray-500 truncate text-[0.7rem] max-w-[50%]">
                    <BsUpcScan className="inline-block mr-1" size={10} />
                    {product.barcode || "|||||||||||||||"}
                  </small>
                  )}
                  {showSku && (
                  <small className="text-gray-500 truncate text-[0.7rem] max-w-[45%]">
                    SKU: {product.sku || "****"}
                  </small>
                  )}
                </div>
                )}

                {showStock && showStockCol && (
                  <div className="text-center mb-1">
                    <span className="text-xs font-medium text-gray-600">
                      Stock: {parseFloat(product.stock || 0)} {product.unit?.symbol || ''}
                      {parseFloat(product.reserved || 0) > 0 && (
                        <span className="text-amber-600"> (-{parseFloat(product.reserved)})</span>
                      )}
                    </span>
                  </div>
                )}

                {/* Base Unit Price - Only show when authenticated */}
                {isAuthenticated && (showPrice || discountBadgeButton) && (
                  <div className="text-center mb-2 py-1 bg-gray-50 rounded">
                    {!showPrice ? discountBadgeButton : appliedDiscount ? (
                      <>
                        <span className="text-sm text-gray-400 line-through mr-1">
                          £{appliedDiscount.original_price.toFixed(2)}
                        </span>
                        <span className="text-lg font-bold text-green-600">
                          £{appliedDiscount.final_price.toFixed(2)}
                        </span>
                        <span className="text-xs text-green-600 ml-1">
                          (-{appliedDiscount.discount_percent.toFixed(0)}%)
                        </span>
                        <span className="text-sm text-gray-600 ml-1">
                          / {product.unit?.symbol || 'unit'}
                        </span>
                      </>
                    ) : discountBadgeInfo?.type === 'fixed_price' && discountBadgeInfo.originalPrice ? (
                      <>
                        <span className="text-sm text-gray-400 line-through mr-1">
                          £{discountBadgeInfo.originalPrice.toFixed(2)}
                        </span>
                        <span className="text-lg font-bold text-green-600">
                          £{discountBadgeInfo.newPrice.toFixed(2)}
                        </span>
                        {selectedCartItem && discountBadgeInfo.newPrice < discountBadgeInfo.originalPrice && (
                          <span className="text-xs text-green-600 ml-1">
                            (-{Math.round((1 - discountBadgeInfo.newPrice / discountBadgeInfo.originalPrice) * 100)}%)
                          </span>
                        )}
                        <span className="text-sm text-gray-600 ml-1">
                          / {product.unit?.symbol || 'unit'}
                        </span>
                        {discountBadgeButton}
                      </>
                    ) : (
                      <>
                        <span className="text-lg font-bold text-gray-900">
                          £{getEffectivePrice(product).toFixed(2)}
                        </span>
                        <span className="text-sm text-gray-600 ml-1">
                          / {product.unit?.symbol || 'unit'}
                        </span>
                        {discountBadgeButton}
                      </>
                    )}
                  </div>
                )}

                {/* Unit Selector & Cart Actions */}
                {isAuthenticated && product.alcohol_blocked ? (
                  showLicenceNotice ? (
                  <div className="mt-auto min-h-[38px] flex items-center justify-center text-center text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2">
                    Premises licence required
                  </div>
                  ) : null
                ) : isAuthenticated ? (
                  <>
                    {/* Unit Selector Buttons */}
                    {unitState !== 'hidden' && (
                    <div className="mt-1 mb-2">
                      <div className="flex flex-wrap justify-center">
                        {availableUnits.map((unitObj) => {
                          if (!unitObj || !unitObj.symbol) return null;

                          const quantityInCart = getQuantityInCart(productId, unitObj.symbol);
                          const unitPrice = getEffectivePrice(product) * parseFloat(unitObj.conversion_factor || 1);
                          const isSelected = selectedUnitSymbol === unitObj.symbol;
                          const cf = parseFloat(unitObj.conversion_factor || 1);
                          const unitDisplay = cf === 1
                            ? unitObj.symbol
                            : `1 ${unitObj.symbol} (${[cf, product.unit?.symbol].filter(Boolean).join(" ")})`;
                          
                          return (
                            <div
                              key={unitObj.id || unitObj.symbol}
                              className="m-1"
                              style={{ minHeight: "38px", minWidth: "60px" }}
                            >
                              <button
                                type="button"
                                className={`relative w-full h-full flex flex-col items-center justify-center transition-none
                                  ${isSelected
                                    ? "bg-blue-600 text-white"
                                    : quantityInCart > 0
                                      ? "border border-blue-600 text-blue-600"
                                      : "border border-gray-200 text-gray-700"
                                  } text-sm font-semibold py-1 px-2 rounded`}
                                onClick={async () => {
                                  // If clicking already selected unit, add +1
                                  if (selectedUnitSymbol === unitObj.symbol) {
                                    const cartItem = getCartItem(productId, unitObj.symbol);
                                    if (cartItem) {
                                      handleQuantityChange(cartItem, 1, productId);
                                    } else {
                                      onCartOperationStart?.();
                                      try {
                                        await handleAddToCart(product, unitObj, 1, unitPrice);
                                        onCartOperationEnd?.();
                                      } catch (error) {
                                        onCartOperationEnd?.();
                                        throw error;
                                      }
                                    }
                                  } else {
                                    // Clicking different unit - switch unit (this will convert existing items)
                                    const itemWasConverted = await handleUnitSelect(productId, unitObj.symbol, product, unitObj);
                                    
                                    // If no items were converted (no existing items), add 1
                                    if (!itemWasConverted) {
                                      onCartOperationStart?.();
                                      try {
                                        await handleAddToCart(product, unitObj, 1, unitPrice);
                                        onCartOperationEnd?.();
                                      } catch (error) {
                                        onCartOperationEnd?.();
                                        throw error;
                                      }
                                    }
                                  }
                                }}
                                disabled={actionState.loading || unitState === 'readonly' || addState !== 'visible' || (isSelected && quantityInCart > 0 && quantityState !== 'editable')}
                                title={showPrice ? `${unitDisplay} - £${unitPrice.toFixed(2)}` : unitDisplay}
                              >
                                <span className="font-bold">{unitDisplay}</span>

                                {showCartQuantity && quantityInCart > 0 && (
                                  <span
                                    className="absolute z-10 -top-2 -right-2 bg-yellow-500 text-black text-sm rounded-full h-6 w-6 flex items-center justify-center font-bold shadow-lg border-2 border-white"
                                  >
                                    {quantityInCart}
                                  </span>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    )}
                    
                    {/* Cart Controls */}
                    <div className="mt-auto min-h-[38px]">
                      {selectedQuantity > 0 ? (
                        // Quantity Controls
                        <div className="flex items-center justify-between">
                          {/* Delete Button - 15% */}
                          {removeState !== 'hidden' && (
                          <button
                            className="border border-red-600 text-red-600 hover:bg-red-50 p-1 flex items-center justify-center w-[15%] h-[38px] transition-none rounded"
                            onClick={async () => {
                              onCartOperationStart?.();
                              try {
                                await handleRemoveFromCart(selectedCartItem.id, productId);
                                onCartOperationEnd?.();
                              } catch (error) {
                                onCartOperationEnd?.();
                                throw error;
                              }
                            }}
                            disabled={actionState.loading || removeState === 'disabled'}
                          >
                            <FiTrash2 />
                          </button>
                          )}
                          
                          {quantityState !== 'hidden' && (<>
                          {/* Decrease Button - 20% */}
                          <button
                            className="bg-red-600 text-white hover:bg-red-700 flex items-center justify-center w-[20%] h-[38px] transition-none rounded"
                            onClick={() => handleQuantityChange(selectedCartItem, -1, productId)}
                            disabled={actionState.loading || quantityState === 'readonly'}
                          >
                            <FiMinus />
                          </button>
                          
                          {/* Increase Button - 20% */}
                          <button
                            className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] transition-none rounded"
                            onClick={() => handleQuantityChange(selectedCartItem, 1, productId)}
                            disabled={actionState.loading || quantityState === 'readonly'}
                          >
                            <FiPlus />
                          </button>

                          {/* Increase by 5 - 20% */}
                          <button
                            className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] transition-none rounded"
                            onClick={() => handleQuantityChange(selectedCartItem, 5, productId)}
                            disabled={actionState.loading || quantityState === 'readonly'}
                          >
                            <FiPlus />5
                          </button>
                          
                          {/* Increase by 10 - 20% */}
                          <button
                            className="bg-green-600 text-white hover:bg-green-700 flex items-center justify-center w-[20%] h-[38px] transition-none rounded"
                            onClick={() => handleQuantityChange(selectedCartItem, 10, productId)}
                            disabled={actionState.loading || quantityState === 'readonly'}
                          >
                            <FiPlus />10
                          </button>
                          </>)}
                        </div>
                      ) : (
                        // Add to Cart Button - Full Width
                        addState !== 'hidden' && (
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white w-full h-[38px] transition-none rounded"
                          onClick={async () => {
                            onCartOperationStart?.();
                            try {
                              const unitPrice = getEffectivePrice(product) * parseFloat(selectedUnitObj.conversion_factor || 1);
                              await handleAddToCart(product, selectedUnitObj, 1, unitPrice);
                              onCartOperationEnd?.();
                            } catch (error) {
                              onCartOperationEnd?.();
                              throw error;
                            }
                          }}
                          disabled={actionState.loading || addState === 'disabled'}
                        >
                          {actionState.loading ? 'Adding...' : 'Add to Cart'}
                        </button>
                        )
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GridView;